import CustomSnackbar from "components/snackbar";
import { State } from "data/reducers/state";
import { getCountriesList } from "data/slices/login/api";
import { UserDetailsActions } from "data/slices/userdetails";
import { UserDetailsStateProps } from "data/slices/userdetails/interface";
import { updateUserDetailsApi } from "data/slices/userupdate/api";
import { UpdateUserDetailsResponse } from "data/slices/userupdate/interface";
import { WalletDetailsActions } from "data/slices/wallet";
import { WalletDetailsStateProps } from "data/slices/wallet/interface";
import keys from "locale/keys";
import { isEmpty, isUndefined } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  DASHBOARD_DEFAULT_ROUTE,
  SNACKBAR_TYPE,
  STORAGE_IS_FIRST_LOGIN,
  STORAGE_IS_INITIAL_SURVEY,
} from "utils/constants";
import {
  getDateFormat,
  getFromLocalStorage,
  getUserName,
  saveToLocalStorage,
  walletBalance,
} from "utils/Utils";
import PURE from "widgets/welcome";
import InitialSurveyContainer from "widgets/welcome/initialsurvey/InitialSurveyContainer";
import { UserDetailsProps } from "widgets/welcome/interface";

const WelcomeContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [countriesList, setCountriesList] = useState<string[]>([]);

  // Get stored values for initialSurvey and isFirstLogin
  // Note: initialSurvey is true when the initial survey has been completed
  const initialSurvey =
    getFromLocalStorage(STORAGE_IS_INITIAL_SURVEY) === "true";
  const isFirstLogin = getFromLocalStorage(STORAGE_IS_FIRST_LOGIN) === "true";

  const { details, isFetching } = useSelector<State, UserDetailsStateProps>(
    (state) => state.userdetails,
  );

  const { isFetching: walletDetailsFetching, walletdetails } = useSelector<
    State,
    WalletDetailsStateProps
  >((state) => state.walletdetails);

  useEffect(() => {
    //dont call if user details already fetched, instead directly redirect to dashboard
    if (!details.dateOfBirth) {
      dispatch(UserDetailsActions.request());
    }
    getAllCountriesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    //dont call if wallet details already fetched
    if (!walletdetails.userId) {
      dispatch(WalletDetailsActions.request());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const navigateToDashboard = (details?: UserDetailsProps) => {
    if (!isEmpty(details?.dob)) {
      updateUserDetails(details);
    } else {
      //directly navigated to dashboard
      navigate(DASHBOARD_DEFAULT_ROUTE, {
        replace: true,
      });
    }
  };

  const getAllCountriesList = async () => {
    const allCountriesList = await getCountriesList();

    if (allCountriesList) {
      var countryArr = [];
      allCountriesList.forEach((CountryObject) => {
        countryArr.push(CountryObject.country);
      });
      countryArr.sort();
      setCountriesList(countryArr);
    }
  };

  const updateUserDetails = async (details: UserDetailsProps) => {
    setIsLoading(true);
    const response: UpdateUserDetailsResponse = await updateUserDetailsApi({
      dateOfBirth: getDateFormat(details.dob),
      gender: details.gender,
      postCode: details.address,
      maritalStatus: details.martialStatus,
      country: details.country,
      language: "english",
    });

    setIsLoading(false);
    if (isUndefined(response)) {
      setSnackbarMsg(t(keys.generic_error));
    } else if (response.error) {
      setShowSnackbar(true);
      setSnackbarMsg(response.message);
    } else {
      // Mark first login as completed
      saveToLocalStorage(STORAGE_IS_FIRST_LOGIN, "false");
    }
  };

  // Handle completion of the initial survey
  const handleInitialSurveyComplete = (index?: number) => {
    // Mark the initial survey as completed
    saveToLocalStorage(STORAGE_IS_INITIAL_SURVEY, "true");
    // Navigate to dashboard
    navigate(DASHBOARD_DEFAULT_ROUTE, {
      replace: true,
    });
  };

  // Determine which component to render based on initialSurvey and isFirstLogin
  const renderContent = () => {
    // If initialSurvey is false and isFirstLogin is true, show Questionnaire
    if (!initialSurvey && isFirstLogin) {
      return (
        <PURE
          navigateToDashboard={navigateToDashboard}
          userName={getUserName(details?.firstName, details?.lastName)}
          walletBalance={walletBalance(walletdetails)}
          isLoading={isLoading || isFetching || walletDetailsFetching}
          countryList={countriesList}
        />
      );
    }

    // If initialSurvey is false and isFirstLogin is false, show InitialSurveyContainer
    if (!initialSurvey && !isFirstLogin) {
      return (
        <InitialSurveyContainer
          updateSidedrawerItem={handleInitialSurveyComplete}
        />
      );
    }

    // If initialSurvey is true, navigate to dashboard
    if (initialSurvey) {
      // Navigate to dashboard directly
      navigate(DASHBOARD_DEFAULT_ROUTE, {
        replace: true,
      });
      // Return null as we're navigating away
      return null;
    }

    return (
      <PURE
        navigateToDashboard={navigateToDashboard}
        userName={getUserName(details?.firstName, details?.lastName)}
        walletBalance={walletBalance(walletdetails)}
        isLoading={isLoading || isFetching || walletDetailsFetching}
        countryList={countriesList}
      />
    );
  };

  return (
    <>
      {renderContent()}
      <CustomSnackbar
        msg={snackbarMsg}
        type={SNACKBAR_TYPE.ERROR}
        show={showSnackbar}
        dismiss={() => setShowSnackbar(false)}
      />
    </>
  );
};

export default WelcomeContainer;
